import React from 'react'
import { useEscapeKey } from 'components/hooks/useEscapeKey'
import { ModalCloseButton } from 'components/common/modal/ModalCloseButton'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'
import { ReviewTile, type IssueTrackingCTAMode, type SupplierLinkMode } from 'components/review_tiles/ReviewTile'
import { inboxPath } from 'components/routes/path'

interface Props {
  review: ReviewType
  title: string
  onClose: () => void
  testId: string
  recipientField?: React.ReactNode
  showReviewTile?: boolean
  scrollableContent?: React.ReactNode
  footerContent?: React.ReactNode
  scrollRegionRef?: React.Ref<HTMLDivElement>
  issueTrackingCTAMode?: IssueTrackingCTAMode
  onActiveIssueClick?: () => void
  supplierLinkMode?: SupplierLinkMode
  children?: React.ReactNode
}

export const MessageModalLayout: React.FC<Props> = ({
  review,
  title,
  onClose,
  testId,
  recipientField,
  showReviewTile = true,
  scrollableContent,
  footerContent,
  scrollRegionRef,
  issueTrackingCTAMode,
  onActiveIssueClick,
  supplierLinkMode,
  children
}) => {
  const usesSplitLayout = scrollableContent != null || footerContent != null

  useEscapeKey(onClose)

  return (
    <div
      className='message-reviewer-modal-overlay'
      data-testid={testId}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className='message-reviewer-modal'>
        <ModalCloseButton onClick={onClose} />

        <div className='message-reviewer-modal__inner'>
          <div className='message-reviewer-modal__header'>
            <h2 className='message-reviewer-modal__title'>
              {title}
            </h2>

            <a
              href={inboxPath()}
              className='message-reviewer-modal__inbox-button'
            >
              Go to Inbox
            </a>
          </div>

          <div className='message-reviewer-modal__content'>
            {recipientField}

            {usesSplitLayout
              ? (
                <>
                  <div
                    className='message-reviewer-modal__scroll-region'
                    ref={scrollRegionRef}
                  >
                    {showReviewTile && (
                      <div className='message-reviewer-modal__review-fullbleed'>
                        <ReviewTile
                          review={review}
                          displaySupplierName
                          renderMode='messageReviewerModal'
                          issueTrackingCTAMode={issueTrackingCTAMode}
                          onActiveIssueClick={onActiveIssueClick}
                          supplierLinkMode={supplierLinkMode}
                        />
                      </div>
                    )}

                    {scrollableContent}
                  </div>

                  {footerContent != null && (
                    <div className='message-reviewer-modal__footer'>
                      {footerContent}
                    </div>
                  )}
                </>
                )
              : (
                <>
                  {showReviewTile && (
                    <div className='message-reviewer-modal__review-fullbleed'>
                      <ReviewTile
                        review={review}
                        displaySupplierName
                        renderMode='messageReviewerModal'
                        issueTrackingCTAMode={issueTrackingCTAMode}
                        onActiveIssueClick={onActiveIssueClick}
                        supplierLinkMode={supplierLinkMode}
                      />
                    </div>
                  )}

                  {children}
                </>
                )}
          </div>
        </div>
      </div>
    </div>
  )
}
